import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable, of, switchMap } from 'rxjs';

/**
 * Every Vantage list endpoint caps `limit` at 100 (`Query(50, ge=1, le=100)`), so "give me all of
 * them" is a LOOP, never a bigger number.
 *
 * Asking for more does not return more — it 422s. And because these reads almost always sit behind a
 * `catchError(() => of([]))`, the 422 becomes an empty array and the screen renders as though the
 * account simply has no data. That is how the account pricing panel came to show an empty rate table:
 * it asked for `limit=500`, got a 422, and swallowed it.
 *
 * This is the fifth place the same loop was needed, hence one helper. RBP #37.
 */
export const LIST_PAGE_MAX = 100;

/**
 * The rows out of a list response, whatever envelope it arrived in.
 *
 * ⚠️ **The one place a list read decides what "the rows" are.** Three shapes are live at once and a
 * caller cannot know which it will get:
 *
 * 1. a **bare array** — what most routes answer today;
 * 2. `{results, next_cursor}` — the **keyset** envelope vantage-backend#871 moves ~58 list routes to;
 * 3. `{data}` — the older envelope a handful of reads already tolerated by hand.
 *
 * Every caller used to write `Array.isArray(raw) ? raw : []`, which is correct for (1), returns
 * **zero rows** for (2), and silently renders an empty screen rather than an error. #1093 fixed that
 * for the six reads that go through {@link pageAll}; vantage-web#1102 found the other ~56, which back
 * the main CRM lists — work orders, accounts, deals, invoices, quotes. Same failure, no error, on
 * every one of them.
 *
 * This is a strict SUPERSET of the old expression, so adopting it changes nothing about today's
 * responses. Anything that is neither shape still yields no rows: a body that is an error object
 * must not be spread into a list.
 *
 * ⚠️ `T` defaults to `Record<string, unknown>` rather than `unknown`, deliberately. The expression
 * this replaces — `Array.isArray(raw) ? raw : []` with `raw: unknown` — narrowed to `any[]`, so
 * every call site was already handing its mapper an untyped row. Defaulting to the row shape they
 * all actually use keeps those sites compiling without a cast each, and is strictly tighter than the
 * `any[]` they had. A caller with a real row type passes it: `readRows<QuoteRow>(raw)`.
 *
 * @param raw the parsed response body
 * @returns the rows, never null
 */
export function readRows<T = Record<string, unknown>>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw as T[];
  const body = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  if (Array.isArray(body['results'])) return body['results'] as T[];
  if (Array.isArray(body['data'])) return body['data'] as T[];
  return [];
}

/**
 * Is this body a LIST response at all — in any of the three shapes?
 *
 * The companion {@link readRows} deliberately cannot answer this: it flattens "an empty list" and
 * "a body I do not understand" to the same `[]`, which is right for a caller that renders rows and
 * wrong for the two that refuse to conclude from a malformed reply.
 *
 * Both of those exist and both are deliberate: the inspection capture read throws rather than render
 * *"this visit captured nothing"* (#846), and the opportunity-quote check returns `unknown` rather
 * than let a malformed reply enable a second quote on a deal that already has one (#829). They must
 * keep refusing — but a `{results}` envelope is a list they now understand, so they ask this rather
 * than `Array.isArray`.
 */
export function isRowsResponse(raw: unknown): boolean {
  if (Array.isArray(raw)) return true;
  const body = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return Array.isArray(body['results']) || Array.isArray(body['data']);
}

/**
 * The cursor for the NEXT page, out of a keyset body — `null` when there is no next page, and `null`
 * for a bare-array body, which has no cursor to give.
 *
 * ⚠️ **An empty string is not a cursor.** Treating one as truthy pages forever, which is why the
 * length test is here and not left to each caller.
 *
 * Exported because three pagers outside the store layer need exactly this and nothing else: the two
 * settings viewers and the vendor-bills list hold their own page state in signals rather than a
 * store, so `cursorStateFrom` (which builds reducer state) does not fit them. {@link readPage} uses
 * it too, so the rule lives in one place.
 */
export function readNextCursor(raw: unknown): string | null {
  if (Array.isArray(raw)) return null;
  const body = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const next = body['next_cursor'];
  return typeof next === 'string' && next.length > 0 ? next : null;
}

/** How the NEXT page is addressed. Decided by the shape the server answered with, never guessed. */
type At = { readonly kind: 'offset'; readonly offset: number } | { readonly kind: 'cursor'; readonly cursor: string };

/** One page, read out of either wire shape. */
interface Page<T> {
  readonly rows: readonly T[];
  /** The opaque cursor for the next page; `null` on the last page or on an offset-paged response. */
  readonly nextCursor: string | null;
  /** True when the body was the `{results, next_cursor}` envelope — see {@link readPage}. */
  readonly cursorPaged: boolean;
}

/**
 * Read one page out of **either** wire shape, without guessing which endpoint is which.
 *
 * ⚠️ There are two, and the repo has to tolerate both at once. Vantage list endpoints answer a bare
 * array and page by `limit`/`offset`; vantage-backend#871 converts six of them to keyset pagination,
 * which answers `{"results": [...], "next_cursor": string | null}` and pages by `limit`/`cursor`.
 * The two repos deploy independently, so for a window BOTH are live and no caller can know in
 * advance which it is talking to. Reading the response rather than the route is what makes that
 * window uneventful in either merge order.
 *
 * ⚠️ Why `cursorPaged` exists as its own flag rather than being inferred from `nextCursor`: a
 * cursor endpoint's LAST page carries `next_cursor: null`, which is indistinguishable from an
 * offset-paged body by cursor alone — and the offset loop's "a full page might not be the end" rule
 * must not run against it. A cursor endpoint that returns exactly 100 rows on its final page would
 * otherwise be asked for `offset=100`, which it does not declare, so FastAPI would drop the param
 * and hand back **the same first page again, forever** (RBP #38 — an undeclared param is ignored,
 * not rejected). An infinite request loop is a far worse failure than the empty list this change
 * exists to prevent, and it is only reachable through that one branch.
 *
 * Anything that is neither shape yields no rows, exactly as before: a body that is an error object
 * rather than a list must not be spread into the accumulator.
 */
function readPage<T>(raw: unknown): Page<T> {
  if (Array.isArray(raw)) return { rows: raw as T[], nextCursor: null, cursorPaged: false };

  const body = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  if (Array.isArray(body['results'])) {
    return { rows: readRows(raw) as T[], nextCursor: readNextCursor(raw), cursorPaged: true };
  }
  return { rows: [], nextCursor: null, cursorPaged: false };
}

/**
 * Read every page of a list endpoint.
 *
 * @param http    the caller's injected `HttpClient`
 * @param url     absolute endpoint url
 * @param params  filters to send on every page (must NOT include `limit`/`offset`/`cursor` — those are ours)
 */
export function pageAll<T>(
  http: HttpClient,
  url: string,
  params: Record<string, string> = {},
): Observable<T[]> {
  return read<T>(http, url, params, { kind: 'offset', offset: 0 }, []);
}

function read<T>(
  http: HttpClient,
  url: string,
  params: Record<string, string>,
  at: At,
  acc: readonly T[],
): Observable<T[]> {
  let httpParams = new HttpParams().set('limit', String(LIST_PAGE_MAX));
  // The FIRST request still carries `offset=0`, deliberately. Both schemes start at the beginning
  // without a positional param, so omitting it would be tidier — but it would also change the wire
  // for the twenty-odd endpoints that are working today, to buy nothing they need. A cursor route
  // does not declare `offset` and FastAPI drops it, so this costs the new six nothing either.
  httpParams = at.kind === 'offset'
    ? httpParams.set('offset', String(at.offset))
    : httpParams.set('cursor', at.cursor);
  for (const [k, v] of Object.entries(params)) httpParams = httpParams.set(k, v);

  return http.get<unknown>(url, { params: httpParams }).pipe(
    map((raw) => readPage<T>(raw)),
    switchMap((pageRead) => {
      const all = [...acc, ...pageRead.rows];
      if (pageRead.nextCursor !== null) {
        return read<T>(http, url, params, { kind: 'cursor', cursor: pageRead.nextCursor }, all);
      }
      // The server said this is the last page, and only a cursor response can say so.
      if (pageRead.cursorPaged) return of(all);
      // Offset-paged: a short page means the end. A full one might not be, so keep going.
      const nextOffset = (at.kind === 'offset' ? at.offset : 0) + LIST_PAGE_MAX;
      return pageRead.rows.length === LIST_PAGE_MAX
        ? read<T>(http, url, params, { kind: 'offset', offset: nextOffset }, all)
        : of(all);
    }),
  );
}
